import { useEffect, useState } from "react";
import { X, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import newsletterImg from "@/assets/newsletter.webp";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const KEY = "empro_newsletter_v1";

export default function NewsletterPopup() {
  const [open, setOpen] = useState(() => {
    try { return !localStorage.getItem(KEY); } catch { return true; }
  });
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [imgSrc, setImgSrc] = useState(newsletterImg);

  useEffect(() => {
    api.get("/shop/newsletter-image")
      .then((r) => { if (r.data?.file_id) setImgSrc(`${API}/files/${r.data.file_id}`); })
      .catch(() => {});
  }, []);

  const close = () => {
    setOpen(false);
    localStorage.setItem(KEY, "closed");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Podaj poprawny adres e-mail");
      return;
    }
    setBusy(true);
    try {
      await api.post("/shop/newsletter", { email: email.trim() });
      setDone(true);
      localStorage.setItem(KEY, "subscribed");
      setTimeout(() => setOpen(false), 2600);
    } catch {
      toast.error("Nie udało się zapisać. Spróbuj ponownie.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="newsletter-popup"
      className="fixed bottom-4 left-4 z-50 w-[min(92vw,360px)] overflow-hidden rounded-2xl border border-white/10 bg-[#111318] shadow-2xl shadow-black/50 transition-transform"
    >
      <button
        type="button"
        data-testid="newsletter-close"
        onClick={close}
        aria-label="Zamknij"
        className="absolute right-2 top-2 z-10 grid h-8 w-8 place-items-center rounded-full bg-black/55 text-white/90 backdrop-blur-sm transition hover:bg-black/80 hover:scale-105"
      >
        <X className="h-4 w-4" />
      </button>

      <img src={imgSrc} onError={(e) => { e.currentTarget.src = newsletterImg; }} alt="Zapisz się do newslettera EMPRO" className="block w-full h-auto select-none" draggable={false} />

      <div className="p-4">
        {done ? (
          <div data-testid="newsletter-success" className="flex items-center gap-2 py-2 text-emerald-400">
            <CheckCircle2 className="h-5 w-5" />
            <span className="font-semibold">Dziękujemy! Jesteś zapisany.</span>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-2.5">
            <Input
              data-testid="newsletter-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Twój adres e-mail"
              className="h-11 bg-white/5 border-white/15 text-neutral-100 placeholder:text-neutral-500"
            />
            <Button
              data-testid="newsletter-submit"
              type="submit"
              disabled={busy}
              className="w-full h-11 gap-2 bg-red-600 hover:bg-red-500 text-white font-bold uppercase tracking-wide"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Zapisz się
            </Button>
            <p className="text-[11px] leading-snug text-neutral-500">
              Zapisując się akceptujesz otrzymywanie informacji handlowych. Możesz zrezygnować w każdej chwili.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
